// eslint-disable-next-line no-restricted-imports
import { Accordion, AccordionDetails, AccordionSummary, Collapse, Stack } from "@mui/material";
import { useLocalStorage } from "@src/appV2/lib/utils";
import { CbhIcon, Text } from "@src/appV2/redesign/system/ui-components";
import { TransitionGroup } from "react-transition-group";

import { LoadingButton } from "../../../components/LoadingButton";
import { PlacementsLoader } from "../PlacementsLoader";

export interface PlacementsAccordionSectionV3Props {
  title: string | React.ReactNode;
  initialExpanded: boolean;
  placementIds: string[];
  renderPlacementCard: (placementId: string, index: number) => React.ReactNode;
  isLoading: boolean;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  onLoadMore?: () => void;
  totalPlacementsCount?: number;
  emptyText: React.ReactNode;
}

export function PlacementsAccordionSectionV3(props: PlacementsAccordionSectionV3Props) {
  const {
    title,
    initialExpanded,
    placementIds,
    isLoading,
    hasNextPage,
    isFetchingNextPage,
    onLoadMore,
    emptyText,
    renderPlacementCard,
    totalPlacementsCount = 0,
  } = props;

  const hasNoPlacements = !isLoading && totalPlacementsCount === 0;

  // Generate storage key based on title - use string version or fallback
  const storageKey = `placements-accordion-section-${
    typeof title === "string" ? title : "section"
  }-expanded`;
  const [expanded, setExpanded] = useLocalStorage(storageKey, initialExpanded ?? false);

  return (
    <Accordion
      disableGutters
      expanded={expanded || hasNoPlacements}
      sx={{
        backgroundColor: (theme) => theme.palette.background.tertiary,
        "&:before": {
          display: "none",
        },
      }}
      onChange={(_, newExpanded) => {
        setExpanded(newExpanded);
      }}
    >
      <AccordionSummary
        expandIcon={<CbhIcon type="chevron-down" />}
        sx={{ minHeight: 40, "& .MuiAccordionSummary-content": { my: 1 } }}
      >
        <Stack direction="row" alignItems="end" spacing={3}>
          {typeof title === "string" ? <Text variant="h5">{title}</Text> : title}
          {typeof title === "string" && (
            <Text variant="body2" sx={{ color: (theme) => theme.palette.text.tertiary }}>
              {totalPlacementsCount}
            </Text>
          )}
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 4 }}>
        <Stack>
          {isLoading && <PlacementsLoader count={1} />}

          {hasNoPlacements && (
            <Stack justifyContent="center" flex={1} alignItems="center" sx={{ pt: 5 }} spacing={2}>
              {emptyText}
            </Stack>
          )}

          {placementIds.length > 0 && (
            <Stack direction="column" spacing={6} component={TransitionGroup}>
              {placementIds.map((placementId, placementIndex) => (
                <Collapse
                  key={placementId}
                  unmountOnExit
                  timeout={{
                    enter: 200,
                    exit: 200,
                  }}
                >
                  <>{renderPlacementCard(placementId, placementIndex)}</>
                </Collapse>
              ))}
            </Stack>
          )}

          {hasNextPage && (
            <Stack direction="row" justifyContent="center">
              <LoadingButton
                sx={{ my: 5, mb: 10 }}
                variant="text"
                isLoading={isFetchingNextPage}
                onClick={onLoadMore}
              >
                Load more
              </LoadingButton>
            </Stack>
          )}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}
